// Module dependencies
import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { featureConfig } from 'config/features';
import { withMapsProvider } from 'contexts/MapsContext';

// UI
import { Spinner } from 'components/common/Spinner';
import DynamicHomeRedirect from 'components/common/DynamicHomeRedirect';

// Utils
import { createCustomerRedirectMiddleware, exceptCustomerProtectMiddleware } from './middlewares/middlewares';
import { RouteDefinition } from './types';
import { getRouteRequiredPermission, isRouteAdminOnly } from 'utils/permissions';

// Lazy loading wrapper with Suspense fallback
const LazyLoad = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={
    <div className="flex items-center justify-center w-full h-96">
      <Spinner size="lg" />
    </div>
  }>
    {children}
  </Suspense>
);

/**
 * Helper to get route permission from centralized config.
 * Falls back to provided permission if not found in config.
 */
const getPermission = (path: string) => getRouteRequiredPermission(path);
const getAdminOnly = (path: string) => isRouteAdminOnly(path);

// Lazy-loaded Pages
const Admin = lazy(() => import('pages/Admin'));
const Tasks = lazy(() => import('pages/Tasks').then(m => ({ default: m.Tasks })));
const Dashboard = lazy(() => import('pages/Dashboard'));
const Schedule = lazy(() => import('pages/Schedule'));
const Inventory = lazy(() => import('pages/Inventory'));
const Accounting = lazy(() => import('pages/Accounting'));

// Work Orders
const WorkOrders = lazy(() => import('pages/WorkOrders'));
const WorkOrderTemplatesView = lazy(() => import('pages/WorkOrders/WorkOrderTemplates'));
// Work Orders with Maps - these pages use location picker/maps
const WorkOrderDetails = lazy(() => 
  import('pages/WorkOrders/WorkOrderDetails').then(m => ({ default: withMapsProvider(m.default) }))
);
const CreateWorkOrder = lazy(() => 
  import('pages/WorkOrders/CreateWorkOrder').then(m => ({ default: withMapsProvider(m.default) }))
);

// Projects
const Projects = lazy(() => import('pages/Projects'));
// Projects with Maps - these pages use location picker/maps
const ProjectDetail = lazy(() => 
  import('pages/Projects/ProjectDetail').then(m => ({ default: withMapsProvider(m.default) }))
);
const NewProject = lazy(() => 
  import('pages/Projects/CreateProject').then(m => ({ default: withMapsProvider(m.default) }))
);

// Leads
const Leads = lazy(() => import('pages/Leads'));
const LeadTemplatesDetail = lazy(() => import('pages/Leads/LeadTemplatesDetail'));
// Leads with Maps - detail page uses location picker
const LeadDetail = lazy(() => 
  import('pages/Leads/LeadDetail').then(m => ({ default: withMapsProvider(m.default) }))
);

// Profile
const Profile = lazy(() => import('pages/Profile'));

// Company with Maps - profile tab uses location picker
const Company = lazy(() => 
  import('pages/Company').then(m => ({ default: withMapsProvider(m.default) }))
);

// Locations with Maps - main feature of this page
const Locations = lazy(() => 
  import('pages/Locations').then(m => ({ default: withMapsProvider(m.default) }))
);

// Project Templates
const ProjectTemplates = lazy(() => import('pages/ProjectTemplates'));
const CreateTemplate = lazy(() => import('pages/ProjectTemplates/CreateTemplate'));
const EditTemplate = lazy(() => import('pages/ProjectTemplates/EditTemplate'));
const ViewTemplate = lazy(() => import('pages/ProjectTemplates/ViewTemplate'));

// Budget Templates
const BudgetTemplates = lazy(() => import('pages/BudgetTemplates'));
const CreateBudgetTemplate = lazy(() => import('pages/BudgetTemplates/CreateBudgetTemplate'));
const BudgetTemplateDetails = lazy(() => import('pages/BudgetTemplates/BudgetTemplateDetails'));

// Quote Templates
const QuoteTemplates = lazy(() => import('pages/QuoteTemplates'));
const CreateQuoteTemplate = lazy(() => import('pages/QuoteTemplates/CreateQuoteTemplate'));
const QuoteTemplateDetails = lazy(() => import('pages/QuoteTemplates/QuoteTemplateDetails'));

/**
 * Admin routes configuration.
 * Permissions are loaded dynamically from the centralized permissions.config.ts
 */
export const adminRoutes: RouteDefinition[] = [
  {
    enabled: true,
    path: "/",
    index: true,
    Component: <DynamicHomeRedirect />,
    // if the logged in user is a customer redirect to /customers
    middlewares: [createCustomerRedirectMiddleware('/customers')]
  },
  {
    enabled: featureConfig.companies,
    path: 'admin',
    Component: <LazyLoad><Admin /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
  },
  {
    enabled: featureConfig.companies,
    path: 'companies',
    Component: <Navigate to="/admin" replace />,
    middlewares: [exceptCustomerProtectMiddleware],
  },
  {
    enabled: featureConfig.dashboard,
    path: 'dashboard',
    Component: <LazyLoad><Dashboard /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/dashboard'),
  },
  {
    enabled: featureConfig.tasks,
    path: 'tasks',
    Component: <LazyLoad><Tasks /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/tasks'),
  },
  {
    enabled: featureConfig.projects,
    path: 'projects',
    Component: <LazyLoad><Projects /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/projects'),
  },
  {
    enabled: featureConfig.projects,
    path: 'projects/new',
    Component: <LazyLoad><NewProject /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/projects/new'),
  },
  {
    enabled: featureConfig.projects,
    path: 'projects/:id',
    Component: <LazyLoad><ProjectDetail /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/projects/:id'),
  },
  {
    enabled: featureConfig.workOrders,
    path: 'work-orders',
    Component: <LazyLoad><WorkOrders /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/work-orders'),
  },
  {
    enabled: featureConfig.workOrders,
    path: 'work-orders/new',
    Component: <LazyLoad><CreateWorkOrder /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/work-orders/new'),
  },
  {
    enabled: featureConfig.workOrders,
    path: 'work-orders/templates',
    Component: <LazyLoad><WorkOrderTemplatesView /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/work-orders/templates'),
  },
  {
    enabled: featureConfig.workOrders,
    path: 'work-orders/:id',
    Component: <LazyLoad><WorkOrderDetails /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/work-orders/:id'),
  },
  {
    enabled: featureConfig.team || featureConfig.documents || featureConfig.leads,
    path: 'company',
    Component: <LazyLoad><Company /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    adminOnly: getAdminOnly('/company'),
  },
  // Redirects from old routes to new /company page
  {
    enabled: featureConfig.team,
    path: 'team',
    Component: <Navigate to="/company?tab=team" replace />,
    middlewares: [exceptCustomerProtectMiddleware],
    adminOnly: getAdminOnly('/team'),
  },
  {
    enabled: featureConfig.schedule,
    path: 'schedule',
    Component: <LazyLoad><Schedule /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/schedule'),
  },
  {
    enabled: featureConfig.inventory,
    path: 'inventory',
    Component: <LazyLoad><Inventory /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/inventory'),
  },
  {
    enabled: featureConfig.accounting,
    path: 'accounting',
    Component: <LazyLoad><Accounting /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/accounting'),
  },
  {
    enabled: featureConfig.documents,
    path: 'documents',
    Component: <Navigate to="/company?tab=documents" replace />,
    middlewares: [exceptCustomerProtectMiddleware],
    adminOnly: getAdminOnly('/documents'),
  },
  {
    enabled: featureConfig.projects,
    path: 'projects/templates',
    Component: <LazyLoad><ProjectTemplates /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/projects/templates'),
  },
  {
    enabled: featureConfig.projects,
    path: 'projects/templates/new',
    Component: <LazyLoad><CreateTemplate /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/projects/templates/new'),
  },
  {
    enabled: featureConfig.projects,
    path: 'projects/templates/:id/edit',
    Component: <LazyLoad><EditTemplate /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/projects/templates/:id/edit'),
  },
  {
    enabled: featureConfig.projects,
    path: 'projects/templates/:id',
    Component: <LazyLoad><ViewTemplate /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/projects/templates/:id'),
  },
  {
    enabled: featureConfig.budgets,
    path: 'budget-templates',
    Component: <LazyLoad><BudgetTemplates /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/budget-templates'),
  },
  {
    enabled: featureConfig.budgets,
    path: 'budget-templates/new',
    Component: <LazyLoad><CreateBudgetTemplate /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/budget-templates/new'),
  },
  {
    enabled: featureConfig.budgets,
    path: 'budget-templates/:id',
    Component: <LazyLoad><BudgetTemplateDetails /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/budget-templates/:id'),
  },
  // Quote Templates
  {
    enabled: featureConfig.leads,
    path: 'quote-templates',
    Component: <LazyLoad><QuoteTemplates /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/quote-templates'),
  },
  {
    enabled: featureConfig.leads,
    path: 'quote-templates/new',
    Component: <LazyLoad><CreateQuoteTemplate /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/quote-templates/new'),
  },
  {
    enabled: featureConfig.leads,
    path: 'quote-templates/:id',
    Component: <LazyLoad><QuoteTemplateDetails /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/quote-templates/:id'),
  },
  {
    enabled: true,
    path: '*',
    Component: <DynamicHomeRedirect />,
    middlewares: [exceptCustomerProtectMiddleware],
  },
  {
    enabled: featureConfig.leads,
    path: 'leads',
    Component: <LazyLoad><Leads /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/leads'),
  },
  {
    enabled: featureConfig.leads,
    path: 'leads/forms',
    Component: <Navigate to="/company?tab=forms" replace />,
    middlewares: [exceptCustomerProtectMiddleware],
    adminOnly: getAdminOnly('/leads/forms'),
  },
  {
    enabled: featureConfig.leads,
    path: 'leads/:id',
    Component: <LazyLoad><LeadDetail /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/leads/:id'),
  },
  {
    enabled: featureConfig.leads,
    path: 'leads/forms/:id',
    Component: <LazyLoad><LeadTemplatesDetail /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    adminOnly: getAdminOnly('/leads/forms/:id'),
  },
  {
    enabled: featureConfig.profile,
    path: 'profile/:id',
    Component: <LazyLoad><Profile /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    // Profile is accessible to all authenticated users (no permission required)
  },
  {
    enabled: featureConfig.locations,
    path: 'location',
    Component: <LazyLoad><Locations /></LazyLoad>,
    middlewares: [exceptCustomerProtectMiddleware],
    requiredPermission: getPermission('/location'),
  },
];
